import { AuthContext } from '@dataplace.ai/features'
import { ILocation, IRange } from '@dataplace.ai/types'
import { useAppDispatch } from 'apps/placeme/src/redux/hooks'
import { RootState } from 'apps/placeme/src/redux/store'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
import { getAxios } from '@dataplace.ai/functions/utils/axios'
import { ENDPOINTS } from 'apps/placeme/src/constants/endpoints'
import { Loader } from 'libs/shared/ui-components/src/atoms'
import { handleMaxRanges } from 'apps/placeme/src/functions/handleMaxRanges'
import { createNewAnalyse } from 'apps/placeme/src/features/ChooseLocationReport/chooseLocationSlice'
import { checkComparedCoinsValue } from 'apps/placeme/src/functions'
import { getTileInfo } from 'apps/placeme/src/functions/getTileInfo'
import { DateRangeSelector } from '..'
import { compareLocationCatchmentAndDataAction, deleteTileAction, fetchRangesAction, fetchWorkspaceUsageValue, saveNewRangeAction, saveTemporaryCatchment, saveTileData } from '../../../slice/analysisSlice'
import { RangeTile } from '../../organisms'
import { TileFooter } from '../../atoms'
import { ITileData } from '../../../slice/@types/ITileData'
import { IRangeAndDateTile } from './@types/IRangeAndDataTile.props'
import { tilesWithoutComparedLocation } from '../AddAnotherLocationModal/constants'

const RangeSelectionWrapper = styled.div(() => css`
  display: flex;
  flex-direction: column;
  justify-content:flex-start;
  padding: 1.25rem;
`)

export const RangeAndDateTile = ({
  userId, category, tile, maxRanges, accepted, setAccepted, isExtraPaid, minDate, maxDate, rangeTileDataCy,
  dateRangeDataCy,
}: IRangeAndDateTile) : JSX.Element => {
  const [dateRanges, setDateRanges] = useState<{startDate?: Date, endDate?: Date}>({})

  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [chosenRange, setChosenRange] = useState<IRange>()
  const [scope, setScope] = useState<IRange[]>()
  const [shouldDataBeRequested, setShouldDataBeRequested] = useState(false)
  const [canComparedLocationBeRequested, setCanComparedLocationBeRequested] = useState(false)
  const [token, setToken] = useState('')
  const [loading, setLoading] = useState(true)
  const authContext = useContext(AuthContext)
  const [isCatchmentRequested, setIsCatchmentRequested] = useState(false)
  const {
    ranges, values, creditsAmount, comparedLocation,
  } = useSelector((state: RootState) => state.analysis)
  const trafficTile = useMemo(() => getTileInfo(tile, category, values), [tile, category, values])

  useEffect(() => {
    authContext.userData?.user?.getIdToken()?.then(response => {
      setToken(response)
    })
  }, [authContext])

  const {
    currentSubscriptionData, analyseId, comparedAnalyseId,
  } = useSelector((state: RootState) => state.location)

  useEffect(() => {
    if (token?.length && !ranges.value.length) dispatch(fetchRangesAction(token, userId))
  }, [token])

  useEffect(() => {
    if (values.length) {
      setChosenRange(values?.find(c => c?.id === category)?.tiles
        ?.find(t => t.id === tile)?.chosenRange || handleMaxRanges(ranges?.value, maxRanges)?.[0] || {})
    }
  }, [JSON.stringify(values), ranges?.value])

  useEffect(() => {
    if (values?.length && ranges?.value) {
      const fetchedMaxRanges = handleMaxRanges(ranges?.value, maxRanges)
      const rangeFromStore = values?.find(c => c?.id === category)?.tiles
        ?.find(t => t.id === tile)?.chosenRange
      if (rangeFromStore) {
        fetchedMaxRanges.push(rangeFromStore)
      }
      setScope(fetchedMaxRanges)
    }
  }, [values, ranges?.value])

  const handleChosenRangeChange = (range?: IRange) => {
    setChosenRange(handleMaxRanges(ranges?.value, maxRanges).find(x =>
      x?.type === range?.type && x?.value === range?.value && range?.type !== 'custom') || range)
  }

  const handleSave = () => {
    if (chosenRange && token?.length && trafficTile) {
      dispatch(saveTemporaryCatchment(undefined))
      setIsCatchmentRequested(true)
      dispatch(saveNewRangeAction(token, userId, category, tile, chosenRange))
      if (comparedLocation?.generatedFromNow && chosenRange?.type !== 'custom' && !tilesWithoutComparedLocation.includes(trafficTile?.id?.split('-')[0])) {
        setCanComparedLocationBeRequested(true)
      } else {
        setShouldDataBeRequested(true)
      }
    }
  }

  const handleDeleteTile = () => {
    dispatch(deleteTileAction(token, category, tile, comparedAnalyseId || analyseId || ''))
  }
  const fetchTileRange = () => values.find(cat => cat.id === category)?.tiles.find(t => t.id === tile)?.chosenRange

  const fetchData = useCallback(async () => {
    const catchmentId = trafficTile?.chosenRange?.catchmentId

    if (shouldDataBeRequested && catchmentId) {
      const body = {
        catchmentId,
        dataRanges: {
          startDate: dateRanges.startDate?.getTime(),
          endDate: dateRanges.endDate?.getTime(),
        },
      }

      let saveData
      const getTileType = (id: string) => id.split('-')[0]

      const endpoint = Object.entries(ENDPOINTS).find((key) => key[0] === `${getTileType(tile).toUpperCase()}_TILE`)?.[1]
      const axiosInstance = await getAxios({
        errCallbackFn: (e) => {
          saveData = {
            loading: false,
            error: e,
            value: null,
          }
        },
      })
      const response = await axiosInstance
        .post<ITileData>(endpoint || '', body)
      if (response) {
        saveData = {
          loading: false,
          error: '',
          value: response.data,
        }
        if (response.status === 204) {
          window?.localStorage.setItem('noDataModal', catchmentId || 'no catchment')
          window?.dispatchEvent(new CustomEvent('noDataModal'))
        }
      }
      dispatch(saveTileData(category, tile, saveData))
      setLoading(false)
      if (token?.length) {
        setTimeout(() => dispatch(fetchWorkspaceUsageValue()), 2000)
      }
      setAccepted(true)
    }
  }, [token, shouldDataBeRequested, values])

  const handleFetchComparedData = useCallback(async () => {
    const comparedAnalyseId = window.localStorage.getItem('comparedAnalyseId')
    try {
      // post project - with compared location - create compared analyse
      if (!comparedAnalyseId) await dispatch(createNewAnalyse(analyseId, comparedLocation?.location))
    }
    finally {
      const catchmentId = trafficTile?.chosenRange?.catchmentId
      dispatch(compareLocationCatchmentAndDataAction(
        token,
        catchmentId || '',
        trafficTile?.chosenRange as IRange,
        trafficTile?.section || '',
        trafficTile?.id || '',
        currentSubscriptionData?.value?.subscriptionId || '',
        comparedLocation?.location as ILocation,
        {
          dataRanges: {
            startDate: dateRanges.startDate?.getTime(),
            endDate: dateRanges.endDate?.getTime(),
          },
        },
      ))
    }
  }, [token, canComparedLocationBeRequested, fetchTileRange()?.catchmentId])

  useEffect(() => {
    if (fetchTileRange()?.catchmentId && shouldDataBeRequested) {
      setLoading(true)
      setShouldDataBeRequested(false)
      fetchData()
    }
  }, [fetchData, fetchTileRange()?.catchmentId, shouldDataBeRequested])

  // compared location
  useEffect(() => {
    if (canComparedLocationBeRequested
        && comparedLocation?.generatedFromNow
        && trafficTile
        && fetchTileRange()?.catchmentId
        && trafficTile.chosenRange?.type !== 'custom'
        && comparedLocation?.location) {
      handleFetchComparedData()
    }
  }, [canComparedLocationBeRequested, fetchTileRange()?.catchmentId])

  const handleSubmit = () => {
    if (creditsAmount) {
      handleSave()
    }
  }

  return (
    <>
      {(ranges?.value?.length && !isCatchmentRequested && scope)
        ? (
          <>
            <RangeSelectionWrapper>
              {!!ranges.value.length && (
                <RangeTile
                  chosenRange={chosenRange || handleMaxRanges(ranges.value, maxRanges)[0]}
                  handleChosenRangeChange={handleChosenRangeChange}
                  maxRanges={maxRanges}
                  ranges={scope}
                  resurceSelectorDataCy={rangeTileDataCy}
                />
              )}
              <DateRangeSelector
                accepted={accepted}
                dataCy={dateRangeDataCy}
                description={t('placeme.traffic_visualization_tile.date_range_description')}
                maxDate={maxDate}
                min={7}
                minDate={minDate}
                onChange={(startDate, endDate) => {
                  setDateRanges({
                    startDate,
                    endDate,
                  }) }}
                setAccepted={setAccepted}
                title={t('placeme.traffic_visualization_tile.date_range_title')}
                value={dateRanges}
              />
            </RangeSelectionWrapper>
            <TileFooter
              disabled={!dateRanges?.startDate || !dateRanges?.endDate}
              isExtraPaid={isExtraPaid}
              isUnlimited={currentSubscriptionData?.value?.planExact?.includes('unlimited') || currentSubscriptionData?.value?.plan === 'white'}
              label={isExtraPaid ? t('generic.apply_and_buy') : t('generic.apply')}
              onAccept={handleSubmit}
              onCancel={() => handleDeleteTile()}
              tile={tile}
              value={checkComparedCoinsValue(
                comparedLocation?.generatedFromNow,
                !!comparedLocation?.location,
                !tilesWithoutComparedLocation.includes(tile?.split('-')[0]),
              ).toString()}
            />
          </>
        )
        : (loading && <Loader />) }

    </>
  )
}
